import { set } from 'lodash-es';
import { getObjectEntries, toLocalDateFromApiDateKey, toZonedISODateKey } from '@lp/features/Scheduler/utils';
import { ServiceRulesViolations, ServiceViolationItem, ViolationDayType } from '@lp/features/Scheduler/types';
import {
  catchParseError,
  getDailyAndWeeklyServiceViolations,
  getViolationComboRangeRuleData,
} from '@lp/features/Scheduler/validation.utils';
import { isDualSlotShiftType } from '@lp/features/Scheduler/SchedulerOMS/utils';
import { formatDate, formatNumberOrdinal } from '@/utils/helpers';
import { OrganType } from '@/api/apis/case';
import {
  ComboRangeRuleViolation,
  OMSShiftType,
  ShiftType,
  ShiftTypeScheduleValidationResult,
  ShiftTypeServiceRulesViolations,
} from '@/api/apis/resources/schedule';
import { shiftTypeNames } from '@/constants/schedule.constants';

type ParseOMSComboRangeRuleArgsType<T extends ShiftType> = {
  violation: ComboRangeRuleViolation;
  shiftType: T;
  slotIndex?: number;
};

export const parseOMSComboRangeRule = <T extends ShiftType>({
  violation,
  shiftType,
  slotIndex,
}: ParseOMSComboRangeRuleArgsType<T>) => {
  console.log('V:', violation);
  const { isBelow, remainCount } = getViolationComboRangeRuleData(violation);
  const isDualSlot = isDualSlotShiftType(shiftType as OMSShiftType) && slotIndex !== undefined;
  const shiftTypeName = shiftTypeNames[shiftType];
  const slotStr = isDualSlot ? ` in ${formatNumberOrdinal(slotIndex)} slot` : '';

  return isBelow
    ? `Insufficient number of OMS ${shiftTypeName} shifts${slotStr}; ${remainCount} more shifts are needed.`
    : `Exceeded the maximum number of OMS ${shiftTypeName} shifts${slotStr}; ${remainCount} shifts over the limit.`;
};

const getOMSShiftTypeServiceViolationMessages = <T extends ShiftType>(
  violations: ShiftTypeServiceRulesViolations<T>,
  slotIndex?: number,
) => {
  return getObjectEntries(violations).flatMap(([shiftType, violation]) => {
    const shiftMessages = [];
    // Take only Heart organ type violations assuming all organs are duplicated for OMS shifts
    const byHeartViolation = violation.byOrganType?.[OrganType.Heart];

    if (byHeartViolation) {
      const organMessage = catchParseError(() =>
        parseOMSComboRangeRule({ violation: byHeartViolation, shiftType, slotIndex }),
      );
      shiftMessages.push(organMessage);
    }

    return shiftMessages;
  });
};

export const transformOMSCountryViolations = <T extends ShiftType>(
  violationsData?: ShiftTypeScheduleValidationResult<T>,
) => {
  const byDayMap: ServiceRulesViolations = {};

  if (violationsData) {
    const violationDayKeys = ['dailyRulesViolations', 'weeklyRulesViolations'] as const;

    violationDayKeys.forEach((violationDayKey) => {
      const dayType = violationDayKey === 'dailyRulesViolations' ? ViolationDayType.Daily : ViolationDayType.Weekly;

      getObjectEntries(violationsData[violationDayKey]).forEach(([daySlotKey, serviceViolations]) => {
        // Due to OMS Shift slots represented by time in keys:
        // 1st slot: '2026-06-01T00:00:00+00:00', 2nd slot: '2026-06-01T12:00:00+00:00'.
        const localDate = toLocalDateFromApiDateKey(daySlotKey);
        const dayKey = toZonedISODateKey(localDate);
        const slotIndex = daySlotKey === dayKey ? 1 : 2;
        const violation = serviceViolations.countryRulesViolations;
        const existingMessages = byDayMap?.[dayType]?.[dayKey]?.messages ?? [];
        const newMessages = getOMSShiftTypeServiceViolationMessages(violation, slotIndex);
        const messages = [...existingMessages, ...newMessages].sort((a, b) => a.localeCompare(b));
        const title = formatDate(localDate, 'MM/dd/yyyy');
        const item: ServiceViolationItem = { day: dayKey, dayType, title, messages };

        set(byDayMap, [dayType, dayKey], item);
      });
    });
  }

  return { byDayMap, dayMessagesList: getDailyAndWeeklyServiceViolations(byDayMap) };
};
